import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  InlineEdit,
  Input,
} from 'components'
import { useAnalytics, useToast } from 'hooks'
import { AlertTriangle, Trash2, User } from 'lucide-react'
import { selectCurrentUser } from 'modules/common'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useState } from 'react'
import { ProfilePictureUpload } from '../components/ProfilePictureUpload'
import { deleteAccountAction, updateCurrentUser } from '../slices'

const DELETE_CONFIRMATION_WORD = 'DELETE'

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="card p-6">
      <div className="mb-5 flex items-center gap-2">
        <span className="text-acid">{icon}</span>
        <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">{title}</h3>
      </div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
        {label}
      </label>
      {children}
    </div>
  )
}

export const ProfilePage = () => {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const { showError } = useToast()
  const user = useAppSelector(selectCurrentUser)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [confirmationInput, setConfirmationInput] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const canConfirmDelete = confirmationInput === DELETE_CONFIRMATION_WORD && !isDeleting

  const changeName = (newName: string) => {
    if (!user) return
    const trimmed = newName.trim()
    // Silent acceptance was the bug: an emptied or 500-char name looked saved
    // while the server kept (or stored) something else. Validate with feedback.
    if (!trimmed) {
      showError("Your name can't be empty.")
      return
    }
    if (trimmed.length > 80) {
      showError('Keep your name under 80 characters.')
      return
    }
    if (trimmed !== user.name) {
      dispatch(updateCurrentUser({ name: trimmed }))
    }
  }

  const handleOpenDeleteDialog = (open: boolean) => {
    setDeleteDialogOpen(open)
    if (!open) {
      setConfirmationInput('')
    }
  }

  const handleDeleteAccount = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Radix AlertDialogAction auto-closes the dialog synchronously when its
    // onClick returns. Block that so a 409 toast lands with the dialog still
    // mounted (user sees the error in context). On success we close the
    // dialog explicitly below.
    event.preventDefault()
    if (!canConfirmDelete) return
    track(AnalyticsEvents.Profile.DELETE_ACCOUNT_REQUESTED)
    setIsDeleting(true)
    const result = await dispatch(deleteAccountAction())
    if (deleteAccountAction.rejected.match(result)) {
      const payload = result.payload as { status?: number; message?: string } | undefined
      showError(payload?.message ?? 'Failed to delete account. Please try again.')
      setIsDeleting(false)
      // Keep dialog open on 409 so user can see the error context, but clear
      // the typed confirmation so the destructive button re-disables.
      setConfirmationInput('')
      return
    }
    // Success: local state is being purged by the thunk; the app shell will
    // redirect to /signin via the auth-state subscriber. Close the dialog
    // explicitly (we blocked the auto-close above) in case the unmount races.
    setDeleteDialogOpen(false)
    setConfirmationInput('')
    setIsDeleting(false)
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-acid" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-[640px] space-y-6 px-6 py-8">
        {/* Header */}
        <div>
          <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Profile
          </span>
          <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
            Your account
          </h1>
          <p className="mt-1 text-[14px] text-paper-dim">Manage your personal account settings.</p>
        </div>

        {/* Avatar + Name */}
        <Section icon={<User size={16} />} title="Account">
          <div className="flex items-center gap-5">
            <ProfilePictureUpload userId={user.id} />
            <div className="min-w-0 flex-1 space-y-4">
              <Field label="Name">
                <InlineEdit
                  className="text-[13.5px] text-paper"
                  defaultValue={user.name}
                  onSave={changeName}
                  placeholder="Enter your name"
                  showControls
                />
              </Field>
              <Field label="Email">
                <p className="text-[13.5px] text-paper">{user.email}</p>
              </Field>
            </div>
          </div>

          <div className="mt-5 border-t border-line pt-5">
            <Field label="Member since">
              <p className="text-[13.5px] text-paper">
                {new Date(user.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </Field>
          </div>
        </Section>

        {/* Danger zone */}
        <div className="rounded-[12px] border border-[color:var(--alert-error-border)] bg-[color:var(--alert-error-bg)] p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">
                Delete account
              </h3>
              <p className="mt-1 text-[12px] text-paper-dim">
                Permanently remove your account and all data. This cannot be undone.
              </p>
            </div>
            <button
              onClick={() => setDeleteDialogOpen(true)}
              className="flex-shrink-0 rounded-[8px] border border-[color:var(--alert-error-border)] px-3 py-1.5 text-[13px] font-medium text-[color:var(--alert-error-text)] transition-colors hover:bg-[color:var(--alert-error-bg)]"
            >
              <span className="flex items-center gap-1.5">
                <Trash2 size={14} />
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={handleOpenDeleteDialog}>
        <AlertDialogContent variant="dark">
          <AlertDialogHeader>
            <AlertDialogTitle
              variant="dark"
              className="flex items-center gap-2 text-[color:var(--alert-error-text)]"
            >
              <AlertTriangle className="h-5 w-5" />
              Delete account
            </AlertDialogTitle>
            <AlertDialogDescription variant="dark">
              This permanently deletes your account, businesses, chats, and uploaded files. This
              cannot be undone. If you have an active subscription, cancel it first in your App
              Store, Google Play, or billing settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="delete-account-confirmation" className="text-[13px] text-paper-dim">
              Type <span className="font-semibold text-paper">{DELETE_CONFIRMATION_WORD}</span> to
              confirm.
            </label>
            <Input
              id="delete-account-confirmation"
              variant="dark"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={confirmationInput}
              onChange={e => setConfirmationInput(e.target.value)}
              placeholder={DELETE_CONFIRMATION_WORD}
              disabled={isDeleting}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={!canConfirmDelete}
              className="border border-[color:var(--alert-error-border)] bg-transparent text-[color:var(--alert-error-text)] hover:bg-[color:var(--alert-error-bg)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isDeleting ? 'Deleting…' : 'Yes, delete my account'}
            </AlertDialogAction>
            <AlertDialogCancel
              disabled={isDeleting}
              className="text-ink-on-acid border-0 bg-acid shadow-[var(--shadow-acid-cta)] hover:-translate-y-px"
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
